import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown } from 'lucide-react';

const dimensions = [
  { label: 'Credibility', score: 847 },
  { label: 'Reliability', score: 912 },
  { label: 'Discipline', score: 783 },
  { label: 'Communication', score: 856 },
];

const AnimatedScore = ({ score, isInView, speed = 2 }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setCurrent(prev => {
          if (prev >= score) {
            clearInterval(interval);
            return score;
          }
          return prev + Math.ceil((score - prev) / 20);
        });
      }, speed);
      return () => clearInterval(interval);
    }, 400);
    return () => clearTimeout(timer);
  }, [isInView, score, speed]);

  return <span>{current}</span>;
};

const scoreColor = (score) => {
  if (score >= 900) return '#1A6B3C';
  if (score >= 800) return '#0F1B2D';
  if (score >= 700) return '#C4A35A';
  return '#9B9589';
};

const scoreLabel = (score) => {
  if (score >= 900) return 'Exceptional';
  if (score >= 800) return 'Excellent';
  if (score >= 700) return 'Good';
  return 'Developing';
};

const TrustScoreSection = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.2 }
    );
    const current = sectionRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  const mainScore = 847;

  return (
    <section
      ref={sectionRef}
      id="trust-score"
      className="section-padding relative overflow-hidden"
      data-testid="trust-score-section"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="section-label mb-5">Trust Score</p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0F1B2D] tracking-tight">
            A composite index of professional conduct
          </h2>
          <p className="text-sm text-[#4A4640] font-medium mt-3 max-w-xl mx-auto">
            Assessed across four dimensions. Verified by the Association. Percentile-ranked against the membership base.
          </p>

          {/* Why is it important? */}
          <button
            onClick={() => setShowWhy(!showWhy)}
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#C4A35A] hover:text-[#0F1B2D] transition-colors"
            data-testid="trust-why-btn"
          >
            Why is this important?
            <ChevronDown size={14} className={`transition-transform duration-200 ${showWhy ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showWhy && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-4 max-w-2xl mx-auto text-left govt-panel-gold p-6" data-testid="trust-why-box">
                  <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
                    Production houses, studios, and casting directors receive hundreds of proposals every week. When two artists have similar portfolios, the one with a verified professional record gets the call. A Trust Score makes that decision objective — not based on connections or hearsay.
                  </p>
                  <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
                    Verified professionals close more deals. Grant committees, festival juries, and commissioning editors increasingly look for independently assessed conduct before committing budgets. Your score signals that you deliver on time, communicate reliably, and operate at an institutional standard — the kind of assurance that turns a meeting into a signed contract.
                  </p>
                  <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
                    Think of it as your professional credit score — it follows you across projects, platforms, and collaborations. The higher your score, the more opportunities come to you instead of you chasing them. Artists who build credibility quietly end up working more, earning more, and building a reputation that compounds over time.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* Score Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="govt-panel p-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield size={28} className="text-[#0F1B2D]" strokeWidth={2} />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#9B9589] font-bold">
                AINAA Trust Score
              </p>
            </div>

            <div className="text-center mb-2">
              <span className="font-['Playfair_Display'] text-8xl font-semibold text-[#0F1B2D] leading-none">
                <AnimatedScore score={mainScore} isInView={isInView} />
              </span>
            </div>

            <div className="text-center mb-6">
              <span
                className="text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1"
                style={{
                  color: scoreColor(mainScore),
                  borderBottom: `2px solid ${scoreColor(mainScore)}`,
                }}
              >
                {scoreLabel(mainScore)}
              </span>
            </div>

            {/* Progress bar with gradient */}
            <div className="relative w-full h-3 bg-[#D8D3CB] mb-6">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${(mainScore / 1000) * 100}%` }}
                transition={{ duration: 1.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
                className="h-full"
                style={{ background: 'linear-gradient(90deg, #0F1B2D, #C4A35A)' }}
              />
              <div className="absolute top-0 h-full w-[2px] bg-[#C4A35A]" style={{ left: '96%' }}>
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] font-bold text-[#C4A35A]">960</span>
              </div>
            </div>

            <div className="flex justify-between text-[8px] font-bold text-[#9B9589] uppercase tracking-wider mb-6">
              <span>0</span>
              <span>250</span>
              <span>500</span>
              <span>750</span>
              <span>1000</span>
            </div>

            {/* Dimensions with gradient bars */}
            <div className="grid grid-cols-2 gap-3">
              {dimensions.map((dim) => (
                <div key={dim.label} className="p-3 border border-[#D8D3CB]">
                  <p className="text-[8px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-1">{dim.label}</p>
                  <span className="font-['Playfair_Display'] text-xl font-semibold" style={{ color: scoreColor(dim.score) }}>
                    <AnimatedScore score={dim.score} isInView={isInView} speed={3} />
                  </span>
                  <div className="w-full h-1.5 bg-[#D8D3CB] mt-2">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(dim.score / 1000) * 100}%` }}
                      transition={{ duration: 1.2, delay: 0.8 }}
                      viewport={{ once: true }}
                      className="h-full"
                      style={{ background: `linear-gradient(90deg, #0F1B2D, ${scoreColor(dim.score)})` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right side — factual info, not salesy */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0F1B2D] mb-6">
              How the Trust Score works
            </h3>

            <div className="space-y-5 mb-8">
              <div className="border-l-2 border-[#0F1B2D] pl-4">
                <p className="text-xs font-bold text-[#0F1B2D] uppercase tracking-wider mb-1">Assessment</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
                  Your score is computed from four dimensions: credibility, reliability, discipline, and communication. Each is assessed independently based on verified professional interactions.
                </p>
              </div>

              <div className="border-l-2 border-[#0F1B2D] pl-4">
                <p className="text-xs font-bold text-[#0F1B2D] uppercase tracking-wider mb-1">Verification</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
                  Scores are verified by the Association. They cannot be self-reported or purchased. The assessment is based on real professional conduct, not self-promotion.
                </p>
              </div>

              <div className="border-l-2 border-[#0F1B2D] pl-4">
                <p className="text-xs font-bold text-[#0F1B2D] uppercase tracking-wider mb-1">Scale</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
                  Scored out of 1,000. Percentile-ranked against the full membership base. The highest score ever recorded is 960. Your score updates continuously as your professional record evolves.
                </p>
              </div>

              <div className="border-l-2 border-[#C4A35A] pl-4">
                <p className="text-xs font-bold text-[#C4A35A] uppercase tracking-wider mb-1">Mobile App</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
                  The AINAA mobile app provides detailed score breakdowns, progress tracking, and personalised improvement recommendations from Specter™.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TrustScoreSection;
